import React, { useState } from "react";
import { X, ShieldAlert } from "lucide-react";
import NumericKeypad from "./NumericKeypad";
import { api, formatEUR } from "@/lib/api";
import { toast } from "sonner";
import usePrecontoPrinter from "@/lib/usePrecontoPrinter";
import { printReceiptViaQz } from "@/lib/printService";
import ScontrinoPreview from "@/components/ScontrinoPreview";

/**
 * Modale nascosta per il Movimento Gestionale (non fiscale).
 * Si apre con il gesto nascosto sulla scritta SCONTRINO in cassa
 * (long-press 2,5s su touch, doppio click su PC).
 * Il PIN è richiesto solo se attivo sulla stampante Preconto (gestionale_require_pin).
 * Alla conferma: chiude tavolo/conto, stampa documento identico al preconto.
 */
export default function HiddenGestionaleModal({ open, onClose, orderId, total, tendered = 0, onSuccess }) {
  const { precontoPrinter } = usePrecontoPrinter();
  const requirePin = precontoPrinter ? precontoPrinter.gestionale_require_pin !== false : true;
  const [pin, setPin] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // { order, tenant } → fallback stampa browser

  if (!open && !preview) return null;

  const submit = async () => {
    if (requirePin && pin.length < 4) {
      toast.error("PIN troppo corto");
      return;
    }
    if (!orderId) {
      toast.error("Ordine non salvato");
      return;
    }
    setBusy(true);
    try {
      const cashTendered = tendered && total && tendered >= total - 0.001 ? tendered : null;
      const r = await api.post(`/orders/${orderId}/close-management`, { pin: requirePin ? pin : null, note: note || null, cash_tendered: cashTendered });
      toast.success(`Movimento gestionale #${r.data.management_number} registrato — conto chiuso`);
      setPin("");
      setNote("");
      onSuccess?.(r.data);
      onClose();
      // Documento identico al preconto, sulla stampante preconto (QZ) o anteprima browser
      let tenant = null;
      try { tenant = (await api.get("/tenant/me")).data; } catch { tenant = null; }
      try {
        const res = await printReceiptViaQz({ tenant: tenant || {}, order: r.data, kind: "management" });
        if (res.ok) toast.success(`Documento stampato su "${res.printer}"`);
        else setPreview({ order: r.data, tenant });
      } catch {
        setPreview({ order: r.data, tenant });
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Errore";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 bg-black/85 flex items-center justify-center p-4 z-[70]"
          onClick={onClose}
          data-testid="gestionale-modal"
        >
          <div className="bg-white rounded-md w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-velta-warning" />
                <h3 className="font-heading text-xl font-semibold">Movimento Gestionale</h3>
              </div>
              <button onClick={onClose} data-testid="gestionale-close"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-velta-mute mb-4">
              Chiude il tavolo/conto come <strong>movimento non fiscale</strong>: non passa dalla stampante fiscale e non entra nella chiusura giornaliera.
              Stampa un documento identico al preconto e viene registrato nelle statistiche interne.
            </p>

            <div className="bg-velta-bg border-2 border-velta-border rounded-md p-4 mb-4 text-center">
              <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-1">Importo movimento</div>
              <div className="font-mono font-bold text-3xl tabular-nums">{formatEUR(total || 0)}</div>
            </div>

            <div className="mb-3">
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Nota (opzionale)</label>
              <input
                data-testid="gestionale-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="es. omaggio, consumo personale, prova cucina..."
                className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]"
              />
            </div>

            {requirePin ? (
              <>
                <div className="mb-3">
                  <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">PIN Gestionale</label>
                  <div
                    data-testid="gestionale-pin-display"
                    className="h-14 w-full bg-velta-bg border-2 border-velta-border rounded-md px-4 flex items-center justify-center font-mono text-3xl tracking-[0.4em] tabular-nums"
                  >
                    {pin ? "•".repeat(pin.length) : <span className="text-velta-mute text-sm tracking-widest">Inserisci PIN</span>}
                  </div>
                </div>
                <NumericKeypad value={pin} onChange={setPin} mode="pin" />
              </>
            ) : (
              <div data-testid="gestionale-nopin-info" className="bg-velta-bg border border-velta-border rounded-md p-3 mb-1 text-xs text-velta-mute">
                PIN disattivato dal titolare (Stampanti &amp; POS → stampante Preconto). Conferma diretta.
              </div>
            )}

            <button
              data-testid="gestionale-confirm"
              onClick={submit}
              disabled={busy || (requirePin && pin.length < 4)}
              className="w-full mt-5 h-14 bg-velta-ink hover:bg-velta-ink/90 text-white rounded-md font-medium text-lg btn-tactile disabled:opacity-40"
            >
              {busy ? "Registro..." : "Chiudi come Movimento Gestionale"}
            </button>
          </div>
        </div>
      )}
      {preview && (
        <ScontrinoPreview order={preview.order} tenant={preview.tenant} fiscal={false} onClose={() => setPreview(null)} />
      )}
    </>
  );
}

/**
 * Hook long-press legacy (touch + mouse). Mantenuto per retro-compatibilità.
 */
export function useLongPress(onTrigger, delayMs = 2500) {
  const timerRef = React.useRef(null);
  const start = React.useCallback(() => {
    if (timerRef.current) return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      onTrigger?.();
    }, delayMs);
  }, [onTrigger, delayMs]);
  const cancel = React.useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);
  React.useEffect(() => () => cancel(), [cancel]);
  return {
    onMouseDown: start,
    onMouseUp: cancel,
    onMouseLeave: cancel,
    onTouchStart: start,
    onTouchEnd: cancel,
    onTouchCancel: cancel,
    onContextMenu: (e) => e.preventDefault(),
  };
}

/**
 * Gesto nascosto Movimento Gestionale:
 * - Touch (tablet Android): pressione prolungata 2,5 s
 * - PC con mouse: doppio click
 */
export function useHiddenGesture(onTrigger, delayMs = 2500) {
  const timerRef = React.useRef(null);
  const start = React.useCallback(() => {
    if (timerRef.current) return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      onTrigger?.();
    }, delayMs);
  }, [onTrigger, delayMs]);
  const cancel = React.useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);
  React.useEffect(() => () => cancel(), [cancel]);
  return {
    onTouchStart: start,
    onTouchEnd: cancel,
    onTouchCancel: cancel,
    onTouchMove: cancel,
    onDoubleClick: (e) => { e.preventDefault(); onTrigger?.(); },
    onContextMenu: (e) => e.preventDefault(),
  };
}
